import React from 'react';
import { DragonState } from '../types/GameTypes';
import RespawnTimer from './RespawnTimer';

interface StatsPanelProps {
  dragonState: DragonState | null;
  respawnDurationHours?: number;
}

const StatsPanel: React.FC<StatsPanelProps> = ({ dragonState, respawnDurationHours }) => {
  if (!dragonState) {
    return (
      <div className="stats-panel p-4 bg-gray-800 rounded-lg shadow-md">
        <p className="text-center text-gray-400">Loading dragon stats...</p>
      </div>
    );
  }

  // If dragon is not alive, show death info
  if (!dragonState.isAlive) {
    return (
      <div className="stats-panel p-4 bg-gray-800 rounded-lg shadow-md text-center">
        <h2 className="text-xl font-bold text-center mb-4 text-red-500">Dragon is Resting</h2>
        <p className="text-gray-400 mb-2">Death Count: {dragonState.deathCount}</p>
        <RespawnTimer 
          deathTimestamp={dragonState.deathTimestamp}
          respawnDurationHours={respawnDurationHours}
        />
      </div>
    );
  }

  // Helper to get color based on score
  const getScoreColor = (score: number) => {
    if (score >= 7) return 'bg-green-500'; // Good
    if (score >= 4) return 'bg-yellow-500'; // Warning
    return 'bg-red-500'; // Critical
  };

  // Helper to get health color
  const getHealthColor = (health: number) => {
    if (health >= 80) return 'bg-green-500'; // Good
    if (health >= 50) return 'bg-yellow-500'; // Warning
    if (health >= 30) return 'bg-orange-500'; // Bad
    return 'bg-red-500'; // Critical
  };

  return (
    <div className="stats-panel p-4 bg-gray-800 rounded-lg shadow-md">
      <h2 className="text-xl font-bold text-center mb-4 text-orange-400">Dragon Stats</h2>
      
      {/* Health bar */}
      <div className="mb-4">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-300">Health</span>
          <span className="text-sm font-medium text-gray-300">{dragonState.health}%</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full ${getHealthColor(dragonState.health)}`} 
            style={{ width: `${dragonState.health}%` }}
          ></div>
        </div>
      </div>
      
      {/* Food score bar */}
      <div className="mb-4">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-300">Food</span>
          <span className="text-sm font-medium text-gray-300">{dragonState.foodScore}/10</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full ${getScoreColor(dragonState.foodScore)}`} 
            style={{ width: `${dragonState.foodScore * 10}%` }}
          ></div>
        </div>
      </div>
      
      {/* Cleanliness score bar */}
      <div className="mb-4">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-300">Cleanliness</span>
          <span className="text-sm font-medium text-gray-300">{dragonState.cleanlinessScore}/10</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full ${getScoreColor(dragonState.cleanlinessScore)}`} 
            style={{ width: `${dragonState.cleanlinessScore * 10}%` }}
          ></div>
        </div>
      </div>
      
      {/* Fun score bar */}
      <div className="mb-4">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-300">Fun</span>
          <span className="text-sm font-medium text-gray-300">{dragonState.funScore}/10</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full ${getScoreColor(dragonState.funScore)}`} 
            style={{ width: `${dragonState.funScore * 10}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default StatsPanel; 